import React, { useEffect, useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { compose } from 'redux';
import gql from 'graphql-tag';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { Alert } from '../../components/other/Alert';

import { useUserContext } from '../../../common/utils/context';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { DetailContainer } from '../../containers';
import { useRefetchOnFocus } from '../../hooks';

import {
	Heading,
	Dialog,
	Text,
} from '../../components';
import { ActionDock, Screen } from '../../design/components';

import { useForm } from '../../../common/utils/hooks';
import { InputNumber } from '../../components/input';
import { formatDate } from '../../../common/utils/utils';
import { getFullMachineName } from '../../../common/utils/machine';
import ConfirmCoffeeMachineValuesItem from './ConfirmCoffeeMachineValuesItem';
import { ValuesConfirmationStatus } from './ConfirmMachineValues';

const CONFIRM_MACHINE_VALUES = gql`
	mutation confirmMachineValues (
		$serviceDocketId: ID!,
		$items: JSON,
		$salesMeter: Int,
		$adminTraining: Boolean,
	) {
		confirmMachineValues (
			serviceDocketId: $serviceDocketId,
			items: $items,
			salesMeter: $salesMeter,
			adminTraining: $adminTraining,
		) {
			id
			isRestocked
			isIngredientRestock
			salesMeter
			pickList {
				id
				pickListItems {
					id
					planogramItem {
						id
						quantity
						parlevel
						capacity
						row
						column
						selection
						enabled
						parlevelUnlocked
					}
					product {
						id
						imageUrl
						HCGCategory
						HFDCategory
						name
						size
						sizeUnit
						measurementUnit
					}
					quantity
					quantityProductSizeUnit
					productSize
					productSizeUnit
					productMeasurementUnit
				}
			}

			machine {
				id
				isOffline
				usesIngredients
			}
		}
	}
`;

const ConfirmCoffeeMachineValues = ({
	data: docket,
	id: docketId,
	setAnyUnsavedChanges,
	setConfimValues,
	refetch,
	anyUnsavedChanges,
	disableActions = false,
	adminTraining = false,
	backLabel,
	onBack,
	banner,
}) => {
	const { tenantSettings } = useUserContext();
	const styles = useThemedStyles(createStyles);

	const { values, changeFormValue } = useForm({ items: {} });

	const { machine, pickList } = docket;
	// eslint-disable-next-line prefer-destructuring
	const pickListItems = pickList.pickListItems; // don't destructure in case picklist doesn't exist

	const [confirmMachineValuesMutation, { loading }] = useMutation(CONFIRM_MACHINE_VALUES);
	useRefetchOnFocus(refetch, { enabled: !anyUnsavedChanges && !loading });

	const setItemValueCreator = useCallback((pickListItemId, field) => value => {
		changeFormValue({
			...values,
			items: {
				...values.items,
				[pickListItemId]: {
					...values.items[pickListItemId],
					[field]: value,
				},
			},
		});

		setAnyUnsavedChanges(true);
	}, [changeFormValue, setAnyUnsavedChanges, values]);

	const setItemValuesCreator = useCallback(pickListItemId => newValues => {
		changeFormValue({
			...values,
			items: {
				...values.items,
				[pickListItemId]: {
					...values.items[pickListItemId],
					...newValues,
				},
			},
		});

		setAnyUnsavedChanges(true);
	}, [changeFormValue, setAnyUnsavedChanges, values]);

	const setSalesMeterValue = useCallback(value => {
		changeFormValue({ salesMeter: value });
		setAnyUnsavedChanges(true);
	}, [changeFormValue, setAnyUnsavedChanges]);

	const salesMeter = useMemo(() => (isValueInit(values.salesMeter) ? values.salesMeter : docket.salesMeter), [values, docket]);
	const initialSalesMeter = useMemo(() => docket?.previousDocket?.salesMeter || 0, [docket]);

	const confirmValues = useCallback(async () => {
		setAnyUnsavedChanges(false);

		await confirmMachineValuesMutation({
			variables: {
				serviceDocketId: docketId,
				// turn product into product.id
				items: Object.keys(values.items).reduce((acc, pickListItemId) => {
					acc[pickListItemId] = {
						...values.items[pickListItemId],
						...(values.items[pickListItemId].product ? { product: values.items[pickListItemId].product.id } : {}),
					};
					return acc;
				}, {}),
				salesMeter,
				...(adminTraining ? { adminTraining: true } : {}),
			},
		});

		return refetch();
	}, [adminTraining, values, salesMeter, refetch, setAnyUnsavedChanges, docketId, confirmMachineValuesMutation]);

	const onPressConfirmValues = useCallback(() => {
		let warningContent = '';

		// show warning if meter difference is higher than 500
		if (initialSalesMeter && (salesMeter - initialSalesMeter > 500)) {
			warningContent += ' The sales meter value is high.';
		}

		// show warning if meter read is lower than the previous docket's one
		else if (initialSalesMeter && ((salesMeter || 0) - initialSalesMeter < 0)) {
			warningContent += ' The sales meter value is lower than the previous read.';
		}

		// show warning if using product size and unit and ingredient value > 10
		if (tenantSettings.useProductSizeUnit && Object.values(values.items).some(item => item.quantity > 1000)) {
			warningContent += ' In at least one selection the ingredient quantity is over 10.';
		}

		if (warningContent) {
			return new Promise(resolve => {
				Alert.alert(
					'Warning',
					`Warning!${warningContent}`,
					[
						{ text: 'Cancel', onPress: () => console.log('Cancel Pressed'), style: 'cancel' },
						{
							text: 'Proceed',
							onPress: async () => {
								const result = await confirmValues();
								resolve(result);
							},
						},
					],
					{ cancelable: false },
				);
			});
		}
		else {
			return confirmValues();
		}
	}, [confirmValues, values, tenantSettings, salesMeter, initialSalesMeter]);

	// pass the confirm action to parent
	useEffect(() => {
		setConfimValues(() => onPressConfirmValues);
	}, [onPressConfirmValues, setConfimValues]);

	const machineUsesIngredients = Boolean(machine.ingredientPlanogram);

	// Get enabled planogram items
	const processedItems = useMemo(
		() => sortItems(pickListItems.filter(item => item.planogramItem.enabled)),
		[pickListItems],
	);

	const isConfirmButtonDisabled = useMemo(() => {
		// if loading mutation
		if (loading) {
			return true;
		}

		if (machine.isOffline) {
			// if meter not put if machine is offline
			if (!isValueInit(salesMeter)) {
				return true;
			}

			// if previous meter higher than current
			if (initialSalesMeter && initialSalesMeter > salesMeter) {
				return true;
			}
		}

		return false;
	}, [machine, loading, initialSalesMeter, salesMeter]);

	return (
		<View style={styles.container}>
			<Screen
				headerRight={<ServiceNoteButton />}
				scroll={false}
				backLabel={backLabel}
				onBack={onBack}
				banner={banner}
				dock={(
					<ActionDock
						action={{
							label: 'Confirm values',
							loading,
							onPress: onPressConfirmValues,
							disabled: isConfirmButtonDisabled || disableActions,
						}}
					/>
				)}
			>
				{ loading && (
					<View style={styles.loadingOverlay} />
				)}
				<KeyboardAwareScrollView
					// "enableResetScrollToCoords" - we need it only in ingredient machines (setting quantities)
					// it breaks normal ones scrolling to the top when closing planogram edit dialog
					// adding this condition for now
					enableResetScrollToCoords={machineUsesIngredients}
					style={styles.scrollView}
					scrollIndicatorInsets={{ right: 1 }}
				>
					<View style={styles.top}>
						<Heading
							heading="Confirm values"
							subHeading={getFullMachineName(machine, machine.place, machine.location)}
						/>
						<ValuesConfirmationStatus isConfirmed={docket.isIngredientRestock} />
						<Text style={styles.sectionTitle}>
							Added ingredients
						</Text>
					</View>
					<View style={styles.list}>
						{
							processedItems.map(item => (
								<ConfirmCoffeeMachineValuesItem
									key={item.id}
									item={item}
									setItemValueCreator={setItemValueCreator}
									setItemValuesCreator={setItemValuesCreator}
									value={values.items[item.id]}
								/>
							))
						}
					</View>
					{
						machine.isOffline && (
							<View style={styles.meter}>
								<View style={styles.meterHeader}>
									<Text style={styles.meterLabel}>
										Sales meter
										{
											initialSalesMeter
												? ` (Last: ${initialSalesMeter} on ${formatDate(docket.previousDocket.planned, 'DAY_MONTHWORD_YEAR')})`
												: null
										}
									</Text>
									{
										isValueInit(salesMeter) && (salesMeter - initialSalesMeter) ? <Text style={styles.vendsText}>{ `${salesMeter - initialSalesMeter} vends` }</Text> : null
									}
								</View>
								<InputNumber
									onChangeInternal={setSalesMeterValue}
									value={salesMeter}
									style={styles.meterInput}
								/>
							</View>
						)
					}
				</KeyboardAwareScrollView>
			</Screen>
			<Dialog />
		</View>
	);
};

const isValueInit = value => value || value === 0;

const sortItems = items => {
	const sortedItems = [...items];
	sortedItems.sort((a, b) => (100 * a.planogramItem.row + a.planogramItem.column) - (100 * b.planogramItem.row + b.planogramItem.column));
	return sortedItems;
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		backgroundColor: colors.canvas,
		flex: 1,
	},
	loadingOverlay: {
		position: 'absolute',
		backgroundColor: colors.canvas,
		opacity: 0.6,
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		zIndex: 999,
	},
	scrollView: {
		backgroundColor: colors.canvas,
	},
	top: {
		paddingHorizontal: space.gutter,
		gap: space.md,
	},
	sectionTitle: {
		...type.sectionTitle,
		color: colors.text,
		paddingBottom: space.xs,
	},
	list: {
		borderTopWidth: 1,
		borderTopColor: colors.border,
	},
	meter: {
		paddingHorizontal: space.gutter,
		paddingVertical: space.lg,
		gap: space.xs,
	},
	meterHeader: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	meterLabel: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	vendsText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	meterInput: {
		width: '100%',
	},
});

export default compose(DetailContainer)(ConfirmCoffeeMachineValues);
